import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Save, Plus, Trash2, Zap, Filter, ArrowRight, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { mockApps, getAutomation, PLATFORM_EVENTS, APP_EVENTS, PLATFORM_ACTIONS, APP_ACTIONS, SAMPLE_TRIGGER_PAYLOAD } from '../mocks/automationData';
const styles = {
  btnPrimary: 'text-white rounded-md font-medium text-sm transition-all bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)]',
  btnGhost: 'text-sm text-[var(--colors-text-secondary)] bg-[var(--colors-background-card)] border border-[var(--colors-border-default)] rounded-md hover:bg-[var(--colors-neutral-50)] transition-colors',
  card: 'bg-[var(--colors-background-card)] border border-[var(--colors-border-light)] rounded-lg shadow-[var(--shadows-sm)]',
  heading: 'text-[var(--colors-text-primary)] font-semibold',
  label: 'block text-sm font-medium text-[var(--colors-text-secondary)] mb-1',
  input: 'w-full border border-[var(--colors-border-default)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--colors-border-focus)] bg-[var(--colors-background-card)]'
};
interface Condition {
  id: number;
  field: string;
  op: string;
  value: string;
}
interface ActionBlock {
  id: number;
  app: string;
  event: string;
  mappings: {
    field: string;
    required: boolean;
    bind: string;
  }[];
}
const DEFAULT_MAPPINGS = [{
  field: 'Invoice ID',
  required: true,
  bind: 'data.metadata.invoice_id'
}, {
  field: 'Amount',
  required: true,
  bind: 'data.amount'
}, {
  field: 'Currency',
  required: false,
  bind: 'data.currency'
}, {
  field: 'Customer email',
  required: false,
  bind: ''
}];
const PAYLOAD_FIELDS = ['data.payment_id', 'data.amount', 'data.currency', 'data.status', 'data.customer.email', 'data.metadata.invoice_id', 'Static value…'];
export function AutomationBuilder() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const existing = id && id !== 'new' ? getAutomation(id) : undefined;
  const [name, setName] = useState(existing?.name || 'Untitled automation');
  const [isActive, setIsActive] = useState(existing?.status === 'Active');
  const [payloadOpen, setPayloadOpen] = useState(true);
  const [testResult, setTestResult] = useState<null | 'ok'>(null);
  const [triggerApp, setTriggerApp] = useState(existing?.triggerApp || 'platform');
  const [triggerEvent, setTriggerEvent] = useState(existing?.triggerEvent || 'Payment Succeeded');
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [logic, setLogic] = useState<'AND' | 'OR'>('AND');
  const [actions, setActions] = useState<ActionBlock[]>([{
    id: 1,
    app: existing?.actionApp || 'quickbooks',
    event: existing?.actionEvent || 'Reconcile Invoice',
    mappings: DEFAULT_MAPPINGS.map(m => ({
      ...m
    }))
  }]);
  const eventsFor = (app: string) => app === 'platform' ? PLATFORM_EVENTS : APP_EVENTS;
  const actionsFor = (app: string) => app === 'platform' ? PLATFORM_ACTIONS : APP_ACTIONS;
  const addCondition = () => setConditions(c => [...c, {
    id: Date.now(),
    field: 'data.amount',
    op: '≥',
    value: ''
  }]);
  const removeCondition = (cid: number) => setConditions(c => c.filter(x => x.id !== cid));
  const addAction = () => setActions(a => [...a, {
    id: Date.now(),
    app: 'platform',
    event: 'Send Email Notification',
    mappings: DEFAULT_MAPPINGS.map(m => ({
      ...m
    }))
  }]);
  const removeAction = (aid: number) => setActions(a => a.length > 1 ? a.filter(x => x.id !== aid) : a);
  const updateMapping = (aid: number, idx: number, bind: string) => setActions(a => a.map(act => act.id === aid ? {
    ...act,
    mappings: act.mappings.map((m, i) => i === idx ? {
      ...m,
      bind
    } : m)
  } : act));
  const StepNode = ({
    icon,
    tone
  }: {
    icon: React.ReactNode;
    tone: string;
  }) => <div className="w-14 h-14 rounded-full bg-[var(--colors-background-card)] border-2 border-[var(--colors-border-default)] flex items-center justify-center flex-shrink-0 shadow-[var(--shadows-sm)] relative z-10" data-id="element-2852">
      <span style={{
      color: tone
    }} data-id="element-2853">
        {icon}
      </span>
    </div>;
  return <div className="max-w-4xl mx-auto p-6 space-y-6" data-id="element-2854">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-[var(--colors-background-card)] p-4 rounded-lg border border-[var(--colors-border-light)] shadow-[var(--shadows-sm)] sticky top-0 z-20" data-id="element-2855">
        <div className="flex items-center gap-3" data-id="element-2856">
          <button onClick={() => navigate('/automations')} className="p-1.5 rounded-md hover:bg-[var(--colors-neutral-100)] text-[var(--colors-text-tertiary)] transition-colors" data-id="element-2857">
            <ArrowLeft className="w-5 h-5" data-id="element-2858" />
          </button>
          <input type="text" value={name} onChange={e => setName(e.target.value)} className="text-lg font-semibold text-[var(--colors-text-primary)] bg-transparent border-none focus:ring-0 p-0 w-64" data-id="element-2859" />
        </div>
        <div className="flex items-center gap-4" data-id="element-2860">
          <div className="flex items-center gap-2" data-id="element-2861">
            <span className="text-sm font-medium text-[var(--colors-text-secondary)]" data-id="element-2862">
              Active
            </span>
            <button onClick={() => setIsActive(v => !v)} className={`w-10 h-5 rounded-full relative transition-colors ${isActive ? 'bg-[var(--colors-success-dark)]' : 'bg-[var(--colors-neutral-300)]'}`} data-id="element-2863">
              <span className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-2864" />
            </button>
          </div>
          <button onClick={() => setTestResult('ok')} className={`${styles.btnGhost} px-4 py-2 flex items-center gap-2`} data-id="element-2865">
            <Play className="w-4 h-4" data-id="element-2866" /> Run test
          </button>
          <button onClick={() => navigate('/automations')} className={`${styles.btnPrimary} px-4 py-2 flex items-center gap-2`} data-id="element-2867">
            <Save className="w-4 h-4" data-id="element-2868" /> Save
          </button>
        </div>
      </div>

      {testResult === 'ok' && <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-[var(--colors-success-light)] text-[var(--colors-success-dark)] text-sm" data-id="element-2869">
          <CheckCircle2 className="w-4 h-4" data-id="element-2870" /> Test run completed — all 3 steps
          passed with sample data.
        </div>}

      <div className="relative pb-4" data-id="element-2871">
        <div className="absolute left-7 top-8 bottom-8 w-0.5 bg-[var(--colors-border-default)] z-0" data-id="element-2872" />

        {/* Trigger */}
        <div className="relative z-10 mb-8 flex gap-6" data-id="element-2873">
          <StepNode icon={<Zap className="w-5 h-5" data-id="element-2875" />} tone="var(--brand-primary)" data-id="element-2874" />
          <div className={`${styles.card} flex-1 overflow-hidden`} data-id="element-2876">
            <div className="bg-[var(--colors-neutral-50)] px-4 py-3 border-b border-[var(--colors-border-light)] font-medium text-[var(--colors-text-primary)]" data-id="element-2877">
              1. Trigger
            </div>
            <div className="p-5 space-y-5" data-id="element-2878">
              <div className="grid grid-cols-2 gap-4" data-id="element-2879">
                <div data-id="element-2880">
                  <label className={styles.label} data-id="element-2881">App</label>
                  <select className={styles.input} value={triggerApp} onChange={e => {
                  setTriggerApp(e.target.value);
                  setTriggerEvent(eventsFor(e.target.value)[0]);
                }} data-id="element-2882">
                    {mockApps.map(a => <option key={a.id} value={a.id} data-id="element-2883">
                        {a.name}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-2884">
                  <label className={styles.label} data-id="element-2885">Event</label>
                  <select className={styles.input} value={triggerEvent} onChange={e => setTriggerEvent(e.target.value)} data-id="element-2886">
                    {eventsFor(triggerApp).map(ev => <option key={ev} data-id="element-2887">{ev}</option>)}
                  </select>
                </div>
              </div>
              <div className="border border-[var(--colors-border-light)] rounded-md overflow-hidden" data-id="element-2888">
                <button onClick={() => setPayloadOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-[var(--colors-text-secondary)] bg-[var(--colors-neutral-50)]" data-id="element-2889">
                  Sample payload preview
                  {payloadOpen ? <ChevronUp className="w-4 h-4" data-id="element-2890" /> : <ChevronDown className="w-4 h-4" data-id="element-2891" />}
                </button>
                {payloadOpen && <pre className="bg-gray-900 text-green-400 p-4 font-mono text-xs overflow-x-auto" data-id="element-2892">
                    {JSON.stringify(SAMPLE_TRIGGER_PAYLOAD, null, 2)}
                  </pre>}
              </div>
            </div>
          </div>
        </div>

        {/* Conditions */}
        <div className="relative z-10 mb-8 flex gap-6" data-id="element-2893">
          <StepNode icon={<Filter className="w-5 h-5" data-id="element-2895" />} tone="var(--colors-text-secondary)" data-id="element-2894" />
          <div className={`${styles.card} flex-1 overflow-hidden`} data-id="element-2896">
            <div className="bg-[var(--colors-neutral-50)] px-4 py-3 border-b border-[var(--colors-border-light)] font-medium text-[var(--colors-text-primary)] flex justify-between items-center" data-id="element-2897">
              <span data-id="element-2898">2. Conditions (optional)</span>
              <button onClick={addCondition} className="text-sm text-[var(--brand-primary)] hover:text-[var(--brand-primaryhover)] flex items-center gap-1" data-id="element-2899">
                <Plus className="w-4 h-4" data-id="element-2900" /> Add condition
              </button>
            </div>
            <div className="p-5" data-id="element-2901">
              {conditions.length === 0 ? <p className="text-sm text-[var(--colors-text-tertiary)]" data-id="element-2902">
                  No conditions — the action always runs.
                </p> : <div className="space-y-3" data-id="element-2903">
                  {conditions.length > 1 && <div className="flex items-center gap-2 text-xs" data-id="element-2904">
                      <span className="text-[var(--colors-text-tertiary)]" data-id="element-2905">
                        Match
                      </span>
                      {(['AND', 'OR'] as const).map(l => <button key={l} onClick={() => setLogic(l)} className={`px-2 py-0.5 rounded font-medium ${logic === l ? 'bg-[var(--brand-primary)] text-white' : 'bg-[var(--colors-neutral-100)] text-[var(--colors-text-secondary)]'}`} data-id="element-2906">
                          {l}
                        </button>)}
                    </div>}
                  {conditions.map(c => <div key={c.id} className="flex items-center gap-2 flex-wrap" data-id="element-2907">
                      <span className="text-sm text-[var(--colors-text-secondary)]" data-id="element-2908">
                        Only continue if
                      </span>
                      <select className={`${styles.input} w-44`} defaultValue={c.field} data-id="element-2909">
                        {PAYLOAD_FIELDS.filter(f => f !== 'Static value…').map(f => <option key={f} data-id="element-2910">{f}</option>)}
                      </select>
                      <select className={`${styles.input} w-28`} defaultValue={c.op} data-id="element-2911">
                        <option data-id="element-2912">≥</option>
                        <option data-id="element-2913">≤</option>
                        <option data-id="element-2914">=</option>
                        <option data-id="element-2915">≠</option>
                        <option data-id="element-2916">contains</option>
                      </select>
                      <input className={`${styles.input} w-28 font-mono`} placeholder="100" defaultValue={c.value} data-id="element-2917" />
                      <button onClick={() => removeCondition(c.id)} className="p-2 text-[var(--colors-error-default)] hover:bg-[var(--colors-error-light)] rounded" data-id="element-2918">
                        <Trash2 className="w-4 h-4" data-id="element-2919" />
                      </button>
                    </div>)}
                </div>}
            </div>
          </div>
        </div>

        {/* Actions */}
        {actions.map((act, ai) => {
        const unmapped = act.mappings.filter(m => m.required && !m.bind);
        return <div key={act.id} className="relative z-10 mb-8 flex gap-6" data-id="element-2920">
              <StepNode icon={<ArrowRight className="w-5 h-5" data-id="element-2922" />} tone="var(--colors-success-dark)" data-id="element-2921" />
              <div className={`${styles.card} flex-1 overflow-hidden`} data-id="element-2923">
                <div className="bg-[var(--colors-neutral-50)] px-4 py-3 border-b border-[var(--colors-border-light)] font-medium text-[var(--colors-text-primary)] flex justify-between items-center" data-id="element-2924">
                  <span data-id="element-2925">
                    {3 + ai}. Action {actions.length > 1 ? ai + 1 : ''}
                  </span>
                  {actions.length > 1 && <button onClick={() => removeAction(act.id)} className="p-1 text-[var(--colors-error-default)] hover:bg-[var(--colors-error-light)] rounded" data-id="element-2926">
                      <Trash2 className="w-4 h-4" data-id="element-2927" />
                    </button>}
                </div>
                <div className="p-5 space-y-6" data-id="element-2928">
                  <div className="grid grid-cols-2 gap-4" data-id="element-2929">
                    <div data-id="element-2930">
                      <label className={styles.label} data-id="element-2931">App</label>
                      <select className={styles.input} value={act.app} onChange={e => setActions(a => a.map(x => x.id === act.id ? {
                    ...x,
                    app: e.target.value,
                    event: actionsFor(e.target.value)[0]
                  } : x))} data-id="element-2932">
                        {mockApps.map(a => <option key={a.id} value={a.id} data-id="element-2933">
                            {a.name}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-2934">
                      <label className={styles.label} data-id="element-2935">Action</label>
                      <select className={styles.input} value={act.event} onChange={e => setActions(a => a.map(x => x.id === act.id ? {
                    ...x,
                    event: e.target.value
                  } : x))} data-id="element-2936">
                        {actionsFor(act.app).map(ev => <option key={ev} data-id="element-2937">{ev}</option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-2938">
                    <h4 className="text-sm font-medium text-[var(--colors-text-primary)] mb-3 border-b border-[var(--colors-border-light)] pb-2" data-id="element-2939">
                      Field mapping
                    </h4>
                    {unmapped.length > 0 && <div className="flex items-center gap-2 text-xs text-[var(--colors-error-dark)] bg-[var(--colors-error-light)] rounded px-3 py-2 mb-3" data-id="element-2940">
                        <AlertTriangle className="w-4 h-4" data-id="element-2941" />
                        {unmapped.length} required field
                        {unmapped.length > 1 ? 's are' : ' is'} unmapped.
                      </div>}
                    <div className="space-y-3" data-id="element-2942">
                      {act.mappings.map((m, idx) => <div key={m.field} className="flex items-center gap-4" data-id="element-2943">
                          <div className="w-1/3 text-sm text-right" data-id="element-2944">
                            <span className="font-medium text-[var(--colors-text-secondary)]" data-id="element-2945">
                              {m.field}
                            </span>
                            {m.required && <span className="text-[var(--colors-error-default)]" data-id="element-2946">
                                {' '}
                                *
                              </span>}
                            <div className="text-[10px] text-[var(--colors-text-tertiary)] font-mono" data-id="element-2947">
                              {m.field === 'Amount' ? 'number' : 'string'}
                            </div>
                          </div>
                          <select className={`${styles.input} w-2/3 font-mono ${m.required && !m.bind ? 'border-[var(--colors-error-default)]' : ''}`} value={m.bind} onChange={e => updateMapping(act.id, idx, e.target.value)} data-id="element-2948">
                            <option value="" data-id="element-2949">— Select binding —</option>
                            {PAYLOAD_FIELDS.map(f => <option key={f} value={f} data-id="element-2950">
                                {f}
                              </option>)}
                          </select>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>;
      })}

        <div className="relative z-10 ml-20" data-id="element-2951">
          <button onClick={addAction} className={`${styles.btnGhost} px-4 py-2 flex items-center gap-2 border-dashed`} data-id="element-2952">
            <Plus className="w-4 h-4" data-id="element-2953" /> Add another action
          </button>
        </div>
      </div>
    </div>;
}